import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';

const Privacy = () => {
  const theme = useTheme();
  useSEO('隐私政策');

  const privacySections = [
    {
      title: '信息收集',
      icon: <VisibilityIcon />,
      content: [
        '我们收集您主动提供的信息，包括但不限于：',
        '• 个人基本信息（姓名、联系方式、身份证件信息）',
        '• 教育背景信息（学历、成绩单、语言能力证明）',
        '• 申请相关文件（推荐信、个人陈述、作品集等）',
        '• 咨询记录和沟通历史',
        '• 网站使用数据（访问记录、页面浏览等）'
      ]
    },
    {
      title: '信息使用',
      icon: <InfoIcon />,
      content: [
        '我们使用收集的信息用于以下目的：',
        '• 提供留学咨询和申请服务',
        '• 处理院校申请和签证办理',
        '• 发送相关通知和更新信息',
        '• 改善服务质量和个人化体验',
        '• 履行法律义务和合规要求',
        '• 进行市场分析和业务优化'
      ]
    },
    {
      title: '信息保护',
      icon: <ShieldIcon />,
      content: [
        '我们采取严格的安全措施保护您的信息：',
        '• 使用SSL加密技术保护数据传输',
        '• 实施访问控制和权限管理',
        '• 定期进行安全审计和漏洞检测',
        '• 对敏感信息进行脱敏处理',
        '• 建立数据备份和恢复机制',
        '• 培训员工遵守隐私保护规范'
      ]
    },
    {
      title: '信息共享',
      icon: <LockIcon />,
      content: [
        '我们仅在以下情况下共享您的信息：',
        '• 获得您的明确同意',
        '• 与合作的院校和机构共享申请相关信息',
        '• 配合政府部门的法律要求',
        '• 保护我们的合法权益和用户安全',
        '• 与可信的第三方服务提供商合作',
        '• 在业务转让或合并时进行必要的信息转移'
      ]
    },
    {
      title: '您的权利',
      icon: <CheckCircleIcon />,
      content: [
        '您对个人信息享有以下权利：',
        '• 查询和访问您的个人信息',
        '• 要求更正或更新不准确的信息',
        '• 要求删除您的个人信息',
        '• 限制或反对某些信息处理活动',
        '• 撤回已给予的同意',
        '• 获得个人信息副本',
        '• 向监管机构投诉'
      ]
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* 英雄区域 */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />
            </Box>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              隐私政策
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              我们重视您的隐私，致力于保护您的个人信息安全
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Chip 
                label="最后更新：2025年1月" 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 引言 */}
        <Paper
          sx={{
            p: 4,
            mb: 6,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            引言
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            希航教育（以下简称"我们"）深知个人信息对您的重要性，我们将严格遵守相关法律法规，
            采取相应的安全保护措施，致力于保护您的个人信息安全可控。本隐私政策将详细说明我们如何收集、
            使用、存储和保护您的个人信息，以及您对个人信息享有的权利。
          </Typography>
        </Paper>

        {/* 隐私政策内容 */}
        <Box sx={{ mb: 6 }}>
          {privacySections.map((section, index) => (
            <Paper
              key={index}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                '&:hover': {
                  boxShadow: theme.shadows[4],
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mr: 2
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {section.title}
                </Typography>
              </Box>
              <List sx={{ pl: 0 }}>
                {section.content.map((item, itemIndex) => (
                  <ListItem key={itemIndex} sx={{ pl: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 24, mr: 1 }}>
                      {item.startsWith('•') ? (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main
                          }}
                        />
                      ) : (
                        <Box sx={{ width: 6 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.6,
                            color: item.startsWith('•') ? 'text.secondary' : 'text.primary',
                            fontWeight: item.startsWith('•') ? 400 : 500
                          }}
                        >
                          {item}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ))}
        </Box>

        {/* 联系我们 */}
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            联系我们
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.8 }}>
            如果您对本隐私政策有任何疑问、意见或建议，或者需要行使您的个人信息权利，
            请通过以下方式与我们联系：
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              邮箱：xihangedu@qq.com
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              电话：0551-62888215
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              地址：合肥市包河区万达广场7号写字楼1805室
            </Typography>
          </Box>
        </Paper>

        <Divider sx={{ my: 6 }} />

        {/* 政策更新 */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            本隐私政策可能会不时更新。我们会在本页面上发布任何隐私政策的更改，
            并在适当情况下通过其他方式通知您。我们建议您定期查看本隐私政策，
            以了解我们如何保护您的信息。
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Privacy;
